import { addMonths, startOfMonth } from "date-fns";

import { EXACT_CATEGORIES } from "models";
import { capitalizeFirstLetter, convertPascalCaseToSnakeCase, getUserRoleName } from "utils/common";

import type { TFunction } from "i18next";
import type { ExactCategory, ExactStandaloneLicense } from "models";
import type { LicenseType } from "modules";
import type { Licenses } from "modules/LocationLicenseOverview/components";
import type { LicenseItem, LicenseResponse } from "modules/LocationLicenseOverview/queryKeys";

const minimumQuantity = 1;

export const getNextMonthlyInvoicingDate = () => startOfMonth(addMonths(new Date(), 1));

export const findExactItemById = (items: LicenseItem[], exactItemId: string) => {
  return items.find((item: LicenseItem) => item.ID === exactItemId);
};

export const calculateCategoryCosts = (items: LicenseItem[], category: ExactCategory) =>
  category.licenses?.reduce((total, license) => {
    const item = findExactItemById(items, license.exact_item_id);
    if (!item) return total;
    const cost = (license.price || item.StandardSalesPrice) * (category.quantity || minimumQuantity);
    return total + cost;
  }, 0) || 0;

export const calculateStandaloneCosts = (items: LicenseItem[], license: ExactStandaloneLicense) => {
  const item = findExactItemById(items, license.exact_item_id);
  if (!item) return 0;
  return (license.price || item.StandardSalesPrice) * license.quantity;
};

export const calculateTotalCosts = (items: LicenseItem[], categories?: ExactCategory[], standalonesLicense?: ExactStandaloneLicense[]) => {
  const categoryCosts = categories?.reduce((total, category) => total + calculateCategoryCosts(items, category), 0) || 0;
  const standaloneCosts = standalonesLicense?.reduce((total, license) => total + calculateStandaloneCosts(items, license), 0) || 0;
  return categoryCosts + standaloneCosts;
};

export const updateLicenseArray = (licenseArray: ExactStandaloneLicense[], params: ExactStandaloneLicense, condition: boolean): ExactStandaloneLicense[] => {
  const index = licenseArray.findIndex(license => license.id === params.id);
  return condition && index >= 0 ? licenseArray.with(index, { ...licenseArray[index], ...params }) : licenseArray;
};

export const updateCategoryArray = (categoryArray: ExactCategory[], params: ExactCategory): ExactCategory[] => {
  const index = categoryArray.findIndex(
    category =>
      category.role_id === params.role_id && category.license_category_type === params.license_category_type && category.dealer_location_id === params.dealer_location_id
  );

  if (index === -1) return [...categoryArray, params];
  if (categoryArray[index].id && !params.id) return categoryArray;

  return categoryArray.with(index, { ...categoryArray[index], ...params });
};

const categoryKeyMap = Object.entries(EXACT_CATEGORIES).reduce<Record<number, string>>((map, [key, value]) => {
  if (typeof value === "number") map[value] = convertPascalCaseToSnakeCase(key);

  return map;
}, {});

export const getLicenseCategoryName = (license_category_type: EXACT_CATEGORIES | 0, t: TFunction) => {
  if (license_category_type === 0) return "";

  const key = categoryKeyMap[license_category_type];

  if (!key) return "";

  const normalizedKey = key.replace(/([a-z])([A-Z])|([A-Z])(?=[A-Z][a-z])/g, "$1$3 $2");
  return t(`v8_${normalizedKey}`) || capitalizeFirstLetter(normalizedKey.replace(/_/g, " "));
};

export const combinedLicenseData = (licensesData: Licenses | undefined, items: LicenseItem[], t: TFunction, isMonthly: boolean): LicenseType[] => {
  if (!licensesData) return [];

  const standaloneLicenses = isMonthly ? licensesData.monthlyStandalones : licensesData.oneTimeStandalones;
  const categories = isMonthly ? licensesData.monthlyCategories : licensesData.oneTimeCategories;

  const combinedData = standaloneLicenses.reduce<LicenseType[]>((acc, item) => {
    const matchedItem = findExactItemById(items, item.exact_item_id);
    acc.push({
      id: item.id,
      categoryID: null,
      exact_item_id: item.exact_item_id,
      code: matchedItem?.Code || "",
      name: matchedItem?.Description || null,
      category: null,
      quantity: item.quantity,
      description: item.description || "",
      dealer_location_name: item.dealer_location_name,
      price: item.price || matchedItem?.StandardSalesPrice || 0,
      totalPrice: (item.price || matchedItem?.StandardSalesPrice || 0) * item.quantity,
      invoiceOn: item.invoice_on,
      role_id: null,
      dealer_location_id: item.dealer_location_id
    });
    return acc;
  }, []);

  categories.reduce<LicenseType[]>((acc, category) => {
    if (category.licenses?.length) {
      category.licenses.reduce((innerAcc, license) => {
        const matchedItem = findExactItemById(items, license.exact_item_id);
        innerAcc.push({
          id: license.id,
          categoryID: category.id ?? null,
          exact_item_id: license.exact_item_id,
          code: matchedItem?.Code || "",
          name: matchedItem?.Description || null,
          category: `${getLicenseCategoryName(category.license_category_type, t)} ${category.role_id ? `(${getUserRoleName(t, category.role_id)})` : ""}`,
          quantity: category.quantity,
          description: license.description || "",
          dealer_location_name: category.dealer_location_name,
          price: license.price || matchedItem?.StandardSalesPrice || 0,
          totalPrice: (license.price || matchedItem?.StandardSalesPrice || 0) * (category.quantity || minimumQuantity),
          invoiceOn: category.invoice_on,
          role_id: category.role_id,
          dealer_location_id: category.dealer_location_id,
          license_category_type: category.license_category_type
        });
        return innerAcc;
      }, acc);
    } else {
      acc.push({
        id: 0,
        categoryID: Number(category.id),
        exact_item_id: "",
        code: "",
        name: null,
        category: `${getLicenseCategoryName(category.license_category_type, t)} ${category.role_id ? `(${getUserRoleName(t, category.role_id)})` : ""}`,
        quantity: category.quantity,
        description: "",
        dealer_location_name: category.dealer_location_name,
        price: 0,
        totalPrice: 0,
        invoiceOn: category.invoice_on,
        role_id: category.role_id,
        dealer_location_id: category.dealer_location_id,
        license_category_type: category.license_category_type
      });
    }
    return acc;
  }, combinedData);

  return combinedData.sort((a, b) => a.code.localeCompare(b.code));
};

export const filterLicenseData = (items: LicenseItem[], value?: string) => {
  const filteredValues: LicenseItem[] = value
    ? items.filter(item => {
        const searchTerm = value.toLowerCase();
        const combined = [item.Code, item.Description].join(" - ").toLowerCase();
        return combined.includes(searchTerm);
      })
    : [];

  return filteredValues;
};

export const transformData = (data: LicenseResponse): Licenses => {
  const { monthlyCategories, oneTimeCategories } = (data.categories ?? []).reduce<{ monthlyCategories: ExactCategory[]; oneTimeCategories: ExactCategory[] }>(
    (acc, item) => {
      if (item.one_time) acc.oneTimeCategories.push(item);
      else acc.monthlyCategories.push(item);
      return acc;
    },
    { monthlyCategories: [], oneTimeCategories: [] }
  );

  const { monthlyStandalones, oneTimeStandalones } = (data.standalone_licenses ?? []).reduce<{
    monthlyStandalones: ExactStandaloneLicense[];
    oneTimeStandalones: ExactStandaloneLicense[];
  }>(
    (acc, item) => {
      if (item.invoice_on) acc.oneTimeStandalones.push(item);
      else acc.monthlyStandalones.push(item);
      return acc;
    },
    { monthlyStandalones: [], oneTimeStandalones: [] }
  );

  return { monthlyCategories, monthlyStandalones, oneTimeCategories, oneTimeStandalones };
};
